import './App.css';
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import { useState, useEffect } from 'react';
import Head from './Head';
import DynamicTitle from './DynamicTitle';
import StatsContainer from './StatsContainer';
import TrackPageVisits from './TrackPageVisits';

const handleAdClick = async (adName, baseUrl) => {
  try {
    // Send POST request to track the click
    const response = await fetch(`${baseUrl}/track-click`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ad_name: adName }),
    });

    const data = await response.json();
    if (data.status === "success") {
      console.log(`Click count for ${adName}: ${data.click_count}`);
    } else {
      console.error(data.message);
    }
  } catch (error) {
    console.error("Error tracking click:", error);
  } finally {
  }
};

function BusinessCard({ ad }) {
  const CardContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column', // Image at the top, content below
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: '300px', // Minimum width for all cards
    maxWidth: '300px', // Fixed width for all cards
    margin: '0 auto', // Center the card
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    overflow: 'hidden',
    wordWrap: 'break-word', // Wrap text properly
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
    },
  }));

  const Image = styled('img')({
    width: '100%', // Full width at the top
    height: ad.size === 'large' ? '200px' : 'auto', // Fixed height for large ads
    borderRadius: '8px 8px 0 0', // Rounded corners only at the top
    objectFit: 'contain', // Ensure the entire image fits within the container
    marginBottom: '16px', // Spacing between image and content
  });

  const Content = styled('div')({
    flex: 1,
    textAlign: 'center', // Center-align content
  });

  return (
    <CardContainer>
      {ad.image_url && ad.size === 'large' && <Image src={ad.image_url} alt={ad.name} />}
      <Content>
        <h2 style={{ margin: 0, color: '#333' }}>{ad.name}</h2>
        <p style={{ margin: '8px 0', color: '#555', wordWrap: 'break-word' }}>{ad.description}</p>
        {ad.phone && <p style={{ margin: 0, color: '#555' }}>📞 {ad.phone}</p>}
        {ad.address && <p style={{ margin: 0, color: '#555' }}>📍 {ad.address}</p>}
      </Content>
    </CardContainer>
  );
}


function BusinessCards({ location, baseUrl }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    if (baseUrl !== '') {
      const fetchAds = async () => {
        console.log('Fetching ads from:', baseUrl);
        try {
          const response = await fetch(`${baseUrl}/ads`);
          console.log('Response:', response);
          if (!response.ok) {
            throw new Error('Failed to fetch ads');
          }
          const data = await response.json();
          setAds(data);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };

      fetchAds();
    }
  }, [baseUrl]);


  if (loading) {
    return <p style={{ textAlign: 'center' }}>Loading ads...</p>;
  }

  if (error) {
    return <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        padding: 0,
        backgroundColor: '#ffffff', // Set background color to white
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <DynamicTitle location={location} />
      <StatsContainer baseUrl={baseUrl} />
      <Grid
        container
        spacing={4}
        justifyContent="center"
        alignItems="start"
      >
        {ads.map((ad) => (
          <Grid key={ad.name} xs={12} sm={6} md={4}>
            <Link href={ad.url} target="_blank" underline="none" onClick={() => handleAdClick(ad.name, baseUrl)}>
              <BusinessCard ad={ad} />
            </Link>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function App() {
  const [domain, setDomain] = useState('');
  const [location, setLocation] = useState('Loading...');
  const [baseUrl, setBaseUrl] = useState('');
  const debug = process.env.NODE_ENV !== 'production';

  // Side effect to set domain and location
  useEffect(() => {
    const currentDomain = window.location.hostname;
    const locationMap = {
      'trentonmohub.com': 'Trenton',
      'www.trentonmohub.com': 'Trenton',
      'carrolltonmohub.com': 'Carrollton',
      'www.carrolltonmohub.com': 'Carrollton',
      'chillicothemohub.com': 'Chillicothe',
      'www.chillicothemohub.com': 'Chillicothe',
      'jamesportmohub.com': 'Jamesport',
      'www.jamesportmohub.com': 'Jamesport',
      'localhost': 'Trenton',
    };

    console.log('Current domain:', currentDomain);

    setDomain(currentDomain);
    // Set the base URL based on the current domain
    // if the current domain is localhost then use the default base URL
    setBaseUrl(currentDomain === 'localhost' ? 'http://127.0.0.1:5000/api' : `https://${currentDomain}/api`);
    setLocation(locationMap[currentDomain] || 'Unknown Location');
  }, []);


  return (
    <div>
      <TrackPageVisits location={location} debug={debug} baseUrl={baseUrl} />
      <Head location={location} domain={domain} />
      <BusinessCards location={location} baseUrl={baseUrl} />
    </div>
  );
}

export default App;
